"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DetailsStep = void 0;
const Step_1 = require("../types/Step");
const ScheduleStep_1 = require("./ScheduleStep");
const Utils_1 = require("../Utils");
const Attribution_1 = require("../Attribution");
const cleave_js_1 = __importDefault(require("cleave.js"));
/* ── vendors this step loads for itself (SEO handoff 2026-09-17, §5) ──
   These used to sit in order-form2.ejs <head>, so every visitor downloaded them on the
   address gate — the screen furthest from ever needing them. They are injected on the
   first unlock/show of this step instead. Keep the cdnjs integrity hashes in step with
   the version in the URL. */
const CHARGEBEE_SRC = 'https://js.chargebee.com/v2/chargebee.js';
const PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_INTEGRITY = 'sha512-q+4liFwdPC/bNdhUpZx6aXDx/h77yEQtn4I1slHydcbZK34nLaR3cAeYSJshoxIOq3mjEf7xJE8YWIUHMn+oCQ==';
/* The worker is NEVER a <script> tag — pdf.js fetches it itself from workerSrc, off the main
   thread. Loading it as a tag (what the head did) ran the whole worker bundle inline. */
const PDFJS_WORKER_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
/**
 * /order2 Details — the merged Review + Payment step (source design §4.4). The order is
 * placed HERE: contact fields (names are collected on this step — the gate stopped
 * asking), Chargebee payment components, billing info and required consents on the
 * left; the cart summary + FCC label aside on the right.
 *
 * Absorbs the retired ConfirmOfferStep (profile/summary/createCustomer/plan-change) and
 * PaymentStep (Chargebee mount/tokenize/createSubscription) classes. Public API kept
 * for siblings (instanceof lookups): setSummaryProfile(), setSummarySelection(), plus
 * Step's lock/unlock/isLocked/onShow. Payment components are self-mounted on
 * unlock/first show — nothing outside this class references them anymore.
 *
 * customerHub MODE short-circuits into the old hub plan-change experience verbatim
 * (simulate/executePlanChange, #current-monthly/#estimated-monthly/#pay-now, parent
 * postMessage) — no contact/payment/terms sections exist in that markup.
 */
class DetailsStep extends Step_1.Step {
    constructor(state) {
        super();
        this.companyGroup = null;
        this.addressBox = null;
        /** Chargebee hosted-field completeness, fed by field change events. */
        this.cbComplete = { number: false, expiry: false, cvv: false };
        this.cbInstance = null;
        this.cbCard = null;
        /** guard: once the CB customer exists, retries after a payment failure must NOT
         *  recreate it (mirrors the old two-step flow where the customer was created at
         *  confirm time and payment ran separately) */
        this.customerCreated = false;
        /* round-1 Chargebee style config — DM Sans / #092349 / #DC2626, verbatim */
        this.paymentComponentsOptions = {
            locale: 'en',
            fonts: ['https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap'],
            placeholder: { number: "4111 1111 1111 1111" },
            classes: {
                focus: 'focus',
                invalid: 'is-invalid',
                empty: 'empty',
                complete: 'is-valid',
            },
            style: {
                base: {
                    color: '#092349',
                    fontWeight: '500',
                    fontFamily: 'DM Sans, Segoe UI, Helvetica Neue, sans-serif',
                    fontSize: '16px',
                    fontSmoothing: 'antialiased',
                    '::placeholder': { color: '#6B7688' },
                },
                invalid: {
                    color: '#DC2626',
                    ':focus': { color: '#DC2626' },
                    '::placeholder': { color: '#DC2626' },
                }
            }
        };
        this.FORM_STATE = state;
        this.form = document.getElementById('details-form');
        this.submit = document.getElementById('details-submit');
        if (state.config.customerHub) {
            this.currentMonthly = document.getElementById('current-monthly');
            this.estimatedMonthly = document.getElementById('estimated-monthly');
            this.youPayNow = document.getElementById('pay-now');
            this.summarySelection = {
                plan: document.getElementById('summary-plan'),
                addons: document.getElementById('summary-addons'),
                promos: document.getElementById('summary-promos'),
            };
        }
        else {
            this.contactInputs = {
                firstName: document.getElementById('details-first-name'),
                lastName: document.getElementById('details-last-name'),
                email: document.getElementById('details-email'),
                phoneNumber: document.getElementById('details-phone'),
                company: document.getElementById('details-company'),
            };
            this.companyGroup = document.getElementById('details-company-group');
            this.addressBox = document.getElementById('details-address');
            this.referralCode = document.getElementById('referral-email');
            this.tosAcknowledge = document.getElementById('tos-acknowledge');
            this.serviceOptIn = document.getElementById('service-opt-in');
            this.message = document.getElementById('details-message');
            this.payCart = document.getElementById('pay-cart');
            this.payCartPlan = document.getElementById('pay-cart-plan');
            this.payCartTier = document.getElementById('pay-cart-tier');
            this.payCartRows = document.getElementById('pay-cart-rows');
            this.payCartMonthly = document.getElementById('pay-cart-monthly');
            this.pcCardNumber = document.getElementById('pc-card-number');
            this.pcExp = document.getElementById('pc-card-expiry');
            this.pcCvv = document.getElementById('pc-card-cvv');
            // source idiom: billing address hidden behind a same-as-service toggle
            this.billingSame = document.getElementById('billing-same');
            this.billAddrBlock = document.getElementById('pay-billaddr');
            this.billingSame?.addEventListener('change', () => {
                this.billAddrBlock?.classList.toggle('d-none', this.billingSame.checked);
                (0, Utils_1.sendScrollAdjustment)();
            });
        }
        this.init();
    }
    init() {
        this.form.addEventListener('submit', async (e) => {
            e.preventDefault();
            if (this.FORM_STATE.config.customerHub) {
                this.executePlanChange();
            }
            else {
                await this.handleSubmit();
            }
            (0, Utils_1.sendScrollAdjustment)();
        });
        if (!this.FORM_STATE.config.customerHub) {
            // mask phone input (gate idiom)
            const cleave = new cleave_js_1.default(this.contactInputs.phoneNumber, {
                numericOnly: true,
                blocks: [0, 3, 0, 3, 4],
                delimiters: ["(", ")", " ", "-"]
            });
            // start neutral (the untouched idiom — validation strips it)
            for (const field of Array.from(this.form.elements)) {
                if (field instanceof HTMLInputElement && field.type !== 'checkbox') {
                    field.classList.add('untouched');
                }
            }
        }
        if (this.FORM_STATE.config.devMode) {
            console.info(`[OF] DetailsStep loaded.`);
        }
    }
    /** Unlocking also background-mounts the payment components — the same moment the
     *  old flow kicked PaymentStep.startPaymentComponents from ExtrasStep.storeState. */
    unlock() {
        super.unlock();
        if (!this.FORM_STATE.config.customerHub) {
            this.startPaymentComponents();
        }
    }
    onShow() {
        if (this.isFirstShow()) {
            // first show events — both funnel checkpoints now share this screen
            (0, Utils_1.sendEvent)('firstShowConfirmOffer', this.FORM_STATE);
            if (!this.FORM_STATE.config.customerHub) {
                (0, Utils_1.sendEvent)('firstShowPayment', this.FORM_STATE);
            }
            // trigger autofill
            if (this.FORM_STATE.config.autofill) {
            }
        }
        if (!this.FORM_STATE.config.customerHub) {
            // safety nets for debug/direct entry: mount components + (re)fill the cart
            // from the current selection (it can change on a back-and-forth)
            this.startPaymentComponents();
            this.populateCart();
        }
        const existingAlert = document.getElementById('plan-change-error-alert');
        if (existingAlert) {
            existingAlert.remove();
        }
    }
    validate() {
        if (this.FORM_STATE.config.customerHub) {
            return true;
        }
        return this.validateContact() && this.validateTerms() && this.validatePayment(false);
    }
    /* ── public sibling API (instanceof lookups from YourInfoStep/ExtrasStep) ── */
    /**
     * Prefill the contact inputs + service-address box from the gate profile (the gate
     * collects name/phone/email, so this step opens already filled in and editable); a
     * gate resubmit must never clobber values the user already typed with empties.
     */
    setSummaryProfile(profileSummary) {
        if (this.FORM_STATE.config.customerHub) {
            return;
        }
        if (!profileSummary)
            return;
        if (this.addressBox) {
            this.addressBox.innerText = profileSummary.address || ' ';
        }
        const assign = (input, value) => {
            if (!input) {
                return;
            }
            // only overwrite with a real value — empties never clobber user input
            if (value) {
                input.value = value;
            }
        };
        assign(this.contactInputs.firstName, profileSummary.firstName);
        assign(this.contactInputs.lastName, profileSummary.lastName);
        assign(this.contactInputs.email, profileSummary.email);
        assign(this.contactInputs.phoneNumber, profileSummary.phoneNumber);
        // business name — only surfaced for commercial serviceability matches
        if (this.FORM_STATE.ugpsData?.rdi === 'COMMERCIAL') {
            this.companyGroup?.classList.remove('d-none');
            assign(this.contactInputs.company, profileSummary.company);
        }
    }
    /**
     * Refresh the order summary for the current selection. Non-hub: fills the cart
     * aside + renders the FCC label + fires the view-cart events. Hub: the old
     * plan-change summary rows + simulatePlanChange, verbatim.
     * (Unlocking stays the caller's job — ExtrasStep unlocks after this resolves.)
     */
    async setSummarySelection(selection) {
        if (!selection) {
            selection = this.FORM_STATE.userInput.selection;
        }
        if (!selection)
            return;
        if (this.FORM_STATE.config.customerHub) {
            this.setHubSummarySelection(selection);
        }
        else {
            this.populateCart(selection);
            // event
            (0, Utils_1.sendGEvent)('view_cart', this.FORM_STATE);
            (0, Utils_1.sendEvent)('confirmedOrder', this.FORM_STATE);
        }
        // generate FCC label if applicable
        if (selection.plan?.fcc) {
            this.generateFCCLabel(selection.plan.fcc);
        }
    }
    /* ── cart (non-hub) ──────────────────────────────────────────────────── */
    /**
     * Fill the cart aside from the selection state: hero band (plan name + tier),
     * one k/v row per line item, monthly total. Promos/MYBUNDLE render 'Included';
     * plan-level additionalLineItems PROMO entries ride along as included rows (the
     * old ConfirmOffer flash-promo fix). First-month math is gone by design — the
     * source cart shows Total + note only.
     */
    populateCart(selection) {
        if (!this.payCart) {
            return;
        }
        if (!selection) {
            selection = this.FORM_STATE.userInput.selection;
        }
        const plan = selection?.plan;
        if (!plan) {
            // nothing selected (debug/direct entry) — hide the empty shell
            this.payCart.style.display = 'none';
            return;
        }
        this.payCart.style.display = '';
        const dollars = (n) => `$${Math.round(n)}`;
        const planTitle = plan.flavor?.title || plan.title;
        const speed = plan.flavor?.subtitle || '';
        // hero band
        this.payCartPlan.innerText = planTitle;
        this.payCartTier.innerText = speed ? `${speed} symmetrical · no contract` : '';
        // k/v rows — .crow, NOT .row (bootstrap-grid owns .row here)
        const row = (k, v, sub) => {
            const el = document.createElement('div');
            el.className = 'crow';
            const kEl = document.createElement('span');
            kEl.className = 'k';
            kEl.innerText = k;
            const vEl = document.createElement('span');
            vEl.className = 'v';
            vEl.innerText = v;
            el.append(kEl, vEl);
            if (sub) {
                const subEl = document.createElement('small');
                subEl.className = 'crow-sub';
                // flavor sub-lines are authored HTML (e.g. the MyBundle override's <b>N months</b>)
                subEl.innerHTML = sub;
                kEl.append(subEl);
            }
            return el;
        };
        const priceLabel = (price) => price > 0 ? `+${dollars(price)}/mo` : 'Included';
        const addons = selection?.addons ?? [];
        const rows = [
            row(speed ? `${planTitle} — ${speed}` : `${planTitle} plan`, `${dollars(plan.monthlyPrice)}/mo`)
        ];
        // same PROMOTION/PRICELOCK/MYBUNDLE partition the old summary used
        for (const addon of addons.filter(a => a.type !== 'PROMOTION' && a.type !== 'PRICELOCK' && a.type !== 'MYBUNDLE')) {
            rows.push(row(addon.flavor?.title || addon.title, priceLabel(addon.price)));
        }
        const promos = addons.filter(a => a.type === 'PROMOTION' || a.type === 'PRICELOCK' || a.type === 'MYBUNDLE');
        // EVERY plan-level line item is a promo the customer is getting. This kept only the ones
        // whose title or flavor description contained the literal word "PROMO", which is why a
        // promo-code coupon named for its code (FIBER25) vanished while "FREEMO Promotion"
        // survived. A statement credit and the sales-agent first month are promos too. Each one
        // names itself through its coupon's meta_data.flavor.title, exactly as FREEMO does.
        if (plan.additionalLineItems) {
            promos.push(...plan.additionalLineItems);
        }
        for (const promo of promos) {
            rows.push(row(promo.flavor?.title || promo.title, priceLabel(promo.price ?? 0), promo.flavor?.bottomText || undefined));
        }
        this.payCartRows.replaceChildren(...rows);
        // monthly total (old ConfirmOffer formula: plan + every selected addon price)
        const addonsPrice = addons.reduce((sum, a) => sum + a.price, 0);
        const monthlyTotal = `${dollars((plan.monthlyPrice || 0) + addonsPrice)}/mo`;
        this.payCartMonthly.innerText = monthlyTotal;
        // optional page-level flavor: UGPS may deliver the cart fine-print note
        // ("With AutoPay + paperless…" is only the built-in fallback)
        const cartNote = this.FORM_STATE.ugpsData?.flavor?.cartNote;
        if (cartNote) {
            const noteEl = this.payCart?.querySelector('.note');
            if (noteEl) {
                noteEl.innerHTML = cartNote;
            }
        }
        // mobile-only compact summary above the authorize moment (≤820px — the cart
        // aside stacks below the CTA there; CSS hides this block on desktop)
        const paySummary = document.getElementById('pay-summary');
        if (paySummary) {
            const prow = (k, v, cls = '') => {
                const el = document.createElement('div');
                el.className = `prow ${cls}`.trim();
                const kEl = document.createElement('span');
                kEl.innerText = k;
                const vEl = document.createElement('b');
                vEl.innerText = v;
                el.append(kEl, vEl);
                return el;
            };
            const summaryRows = [
                prow(speed ? `${planTitle} — ${speed}` : `${planTitle} plan`, `${dollars(plan.monthlyPrice)}/mo`),
            ];
            for (const addon of addons.filter(a => a.type !== 'PROMOTION' && a.type !== 'PRICELOCK' && a.type !== 'MYBUNDLE')) {
                summaryRows.push(prow(this.textOf(addon.flavor?.title) || addon.title, priceLabel(addon.price)));
            }
            summaryRows.push(prow('Total with AutoPay', monthlyTotal, 'total'));
            paySummary.replaceChildren(...summaryRows);
        }
    }
    /** Strip any HTML from a flavor string for text-only slots. */
    textOf(html) {
        if (!html) {
            return '';
        }
        const d = document.createElement('div');
        d.innerHTML = html;
        return d.textContent?.trim() ?? '';
    }
    /* ── hub plan-change summary (old ConfirmOfferStep code paths, verbatim) ── */
    setHubSummarySelection(selection) {
        if (!selection)
            return;
        // set plan + addons
        this.summarySelection.plan.children[1].innerText =
            selection.plan?.detailedTitle ?? `${selection.plan?.flavor.title} @ $${selection.plan?.monthlyPrice}/mo.`;
        let addons = selection.addons.filter(a => a.type !== 'PROMOTION' && a.type !== 'PRICELOCK' && a.type !== 'MYBUNDLE');
        this.summarySelection.addons.children[1].innerHTML =
            (addons.length > 0) ? addons.map(addon => `${addon.flavor.title} @ $${addon.price}/mo.`).join('<br>') : 'None';
        let promos = selection.addons.filter(a => a.type === 'PROMOTION' || a.type === 'PRICELOCK' || a.type === 'MYBUNDLE');
        // EVERY plan-level line item is a promo the customer is getting. This kept only the ones
        // whose title or flavor description contained the literal word "PROMO", which is why a
        // promo-code coupon named for its code (FIBER25) vanished while "FREEMO Promotion"
        // survived. A statement credit and the sales-agent first month are promos too.
        if (selection.plan?.additionalLineItems) {
            promos.push(...selection.plan.additionalLineItems);
        }
        this.summarySelection.promos.children[1].innerHTML =
            (promos.length > 0) ? promos.map(promo => {
                let promoTitle = promo.flavor?.title || promo.title;
                let promoBottomText = promo?.flavor?.bottomText;
                let message = `<span class="d-block">${promoTitle}</span>`;
                if (promoBottomText) {
                    message += `<small class="promo-explainer">${promoBottomText}</small>`;
                }
                return message;
            }).join('') : 'None';
        this.simulatePlanChange(selection);
        let currentRate = Number(this.FORM_STATE.config.additionalParameters.currentRate ?? 0) || 0;
        currentRate.toFixed(2).replace(/\.00$/g, '');
        this.currentMonthly.innerText = `$${currentRate} / Month`;
    }
    async simulatePlanChange(selection) {
        // disable confirm while calculating prices
        this.submit.disabled = true;
        const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
        try {
            let res = await fetch((0, Utils_1.apiUrl)('/api/simulatePlanChange'), {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    "cid": this.FORM_STATE.config.additionalParameters.cid,
                    "offerInternal": selection.plan?.internal,
                    "addonInternals": selection.addons.map(a => a.internal)
                })
            });
            if (res.status !== 200) {
                throw new Error(`Failed to simulate plan change: ${res.status}`);
            }
            let data = await res.json();
            if (data?.code !== 'OK' || isNaN(Number(data.monthly)) || isNaN(Number(data.total))) {
                throw new Error(`Invalid response from simulatePlanChange: ${JSON.stringify(data)}`);
            }
            this.estimatedMonthly.innerText = `${currency.format(data.monthly).replace(/\.00/g, '')} / Month`;
            this.youPayNow.innerText = `${currency.format(data.total)}`;
            this.submit.disabled = false;
        }
        catch (err) {
            this.renderPlanChangeError();
        }
    }
    async executePlanChange(selection) {
        if (!selection) {
            selection = this.FORM_STATE.userInput.selection;
        }
        if (!selection)
            return;
        (0, Utils_1.setLoadingButton)(this.submit, true, 'Changing Your Plan...');
        try {
            let res = await fetch((0, Utils_1.apiUrl)('/api/executePlanChange'), {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    "cid": this.FORM_STATE.config.additionalParameters.cid,
                    "offerInternal": selection.plan?.internal,
                    "addonInternals": selection.addons.map(a => a.internal)
                })
            });
            let data = await res.json();
            if (data.code && data.code === 'OK') {
                window.parent.postMessage(JSON.stringify({ 'planChangeSuccess': true }), '*');
                (0, Utils_1.sendGEvent)('customer-hub-changed_plan_successfully', this.FORM_STATE);
            }
            else {
                if (data.message && data.message === 'API_LIMIT') {
                    this.renderPlanChangeError(`You can only switch your plan once every 10 minutes! Please contact us with any questions or concerns.`);
                }
                else {
                    this.renderPlanChangeError();
                }
                window.parent.postMessage(JSON.stringify({ 'planChangeSuccess': false }), '*');
                (0, Utils_1.sendGEvent)('customer-hub-failed_change_plan_rate_limit', this.FORM_STATE);
            }
        }
        catch (err) {
            (0, Utils_1.setLoadingButton)(this.submit, false);
            this.renderPlanChangeError();
            window.parent.postMessage(JSON.stringify({ 'planChangeSuccess': false }), '*');
            (0, Utils_1.sendGEvent)('customer-hub-failed_change_plan', this.FORM_STATE);
        }
    }
    renderPlanChangeError(message = `We sincerely apologize for the inconvenience &mdash; please contact support for assistance.`) {
        this.submit.disabled = true;
        // remove existing alert if present
        const existingAlert = document.getElementById('plan-change-error-alert');
        if (existingAlert) {
            existingAlert.remove();
        }
        let alert = document.createElement('div');
        alert.id = 'plan-change-error-alert';
        alert.role = 'alert';
        alert.className = 'row alert alert-danger mt-3';
        alert.innerHTML = `
				<h4 class="alert-title px-0">There was an issue processing your change!</h4>
				<hr class="my-3">
				<h6 class="px-0">We were unable to complete your plan change. <strong>You have not been charged.</strong></h6>
				<p class="px-0">${message}</p>
			`;
        this.form.children[0].append(alert);
    }
    /* ── FCC broadband label (pdf.js pipeline, from the old ConfirmOfferStep) ── */
    async generateFCCLabel(data) {
        // callers fire and forget, and pdf.js is now a runtime fetch that can fail. The label is
        // a nice-to-have panel — losing it must not take the order with it.
        let pdf;
        try {
            pdf = await this.PDFtoIMG(data);
        }
        catch (e) {
            console.warn(`[OF] FCC label unavailable:`, e);
            return;
        }
        const img = document.createElement('img');
        img.alt = `Your Offer's FCC Broadband Label`;
        img.src = pdf[0];
        img.classList.add('border', 'border-dark', 'mx-auto', 'fcc-label');
        document.getElementById('fcc-compliance-wrapper')?.replaceChildren(img);
    }
    base64ToArrayBuffer(base64) {
        let binaryString = atob(base64);
        let bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }
    /** Load pdf.js on first use and point it at its own worker.
     *
     *  workerSrc is what the removed head <script> was standing in for: without it pdf.js
     *  falls back to a fake worker and parses the PDF on the main thread. */
    async loadPdfJs() {
        await (0, Utils_1.loadScript)(PDFJS_SRC, {
            integrity: PDFJS_INTEGRITY,
            crossorigin: 'anonymous',
            referrerpolicy: 'no-referrer',
        });
        const pdfjsLib = window['pdfjsLib'];
        pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
        return pdfjsLib;
    }
    async PDFtoIMG(base64) {
        const pdfjsLib = await this.loadPdfJs();
        return new Promise(async (resolve, reject) => {
            const existingPdfBytes = this.base64ToArrayBuffer(base64);
            const fileArray = new Uint8Array(existingPdfBytes);
            const doc = await pdfjsLib.getDocument({
                data: fileArray,
                useSystemFonts: true,
            }).promise;
            const pages = [];
            const count = 1;
            for (let i = 1; i < doc.numPages + 1; i++) {
                const page = await doc.getPage(i);
                const viewport = page.getViewport({ scale: 1.5 });
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const task = page.render({ canvasContext: ctx, viewport: viewport });
                task.promise.then(() => {
                    pages.push(canvas.toDataURL());
                    if (count == doc.numPages) {
                        resolve(pages);
                    }
                });
            }
        });
    }
    /* ── submit chain (non-hub): validate → createCustomer → tokenize+subscribe ── */
    async handleSubmit() {
        // clear any prior staged error
        this.message.innerHTML = '';
        // stage 1: contact (names REQUIRED here — the gate no longer collects them)
        if (!this.validateContact()) {
            return;
        }
        // stage 2: terms + required service consent
        if (!this.validateTerms()) {
            return;
        }
        // stage 3: payment/billing completeness (old PaymentStep validation)
        if (!this.validatePayment()) {
            this.renderError(`Your payment information is invalid. Please check your details and try again.`);
            return;
        }
        // persist the typed contact info into the profile state (createCustomer + all
        // event envelopes read from FORM_STATE.userInput.profile)
        this.syncProfileFromInputs();
        // stage 4: create the customer — once. A payment retry never recreates it.
        if (!this.customerCreated) {
            (0, Utils_1.setLoadingButton)(this.submit, true, 'Creating Customer...');
            const created = await this.createCustomer();
            (0, Utils_1.setLoadingButton)(this.submit, false);
            if (!created) {
                return;
            }
        }
        // stage 5: tokenize the card + create the subscription
        (0, Utils_1.setLoadingButton)(this.submit, true, 'Processing Payment...');
        await this.submitPaymentComponents();
    }
    /**
     * Validate the contact inputs (source idiom: .is-invalid + shake + focus on the
     * first offender). The optional referral input is deliberately not a blocker —
     * old-flow semantics.
     */
    validateContact() {
        for (const key of ['firstName', 'lastName', 'email', 'phoneNumber', 'company']) {
            const input = this.contactInputs[key];
            if (!input) {
                continue;
            }
            // reset validation state
            input.classList.remove('is-invalid', 'is-valid', 'untouched', 'shake');
            // mark field as valid/invalid
            if (!input.validity.valid) {
                input.classList.add('is-invalid');
                // replay the shake — the reflow read restarts the animation on
                // repeated failed submits
                void input.offsetWidth;
                input.classList.add('shake');
                input.focus();
                return false;
            }
            else {
                input.classList.add('is-valid');
            }
        }
        return true;
    }
    /** Terms & Conditions acknowledgement + required service (SMS) consent. */
    validateTerms() {
        let valid = true;
        for (const box of [this.tosAcknowledge, this.serviceOptIn]) {
            box.classList.remove('is-invalid', 'is-valid');
            const container = box.closest('.terms-box');
            container?.classList.remove('shake');
            if (!box.checked) {
                box.classList.add('is-invalid');
                if (container) {
                    void container.offsetWidth;
                    container.classList.add('shake');
                }
                valid = false;
            }
            else {
                box.classList.add('is-valid');
            }
        }
        return valid;
    }
    /**
     * Old PaymentStep validation: every billing input's constraint validity + the
     * Chargebee hosted-field wrappers' runtime classes (.is-invalid/.empty).
     */
    validatePayment(shouldFocus = true) {
        let hasError = false;
        const paymentSec = document.getElementById('details-payment-sec');
        for (let field of Array.from(paymentSec?.querySelectorAll('input') ?? [])) {
            // billing fields are skipped while "same as service address" is on
            // (they're client-side ceremony only — never transmitted)
            if (this.billingSame?.checked && field.closest('#pay-billaddr')) {
                field.classList.remove('is-invalid', 'is-valid', 'untouched');
                continue;
            }
            // reset validation state
            field.classList.remove('is-invalid', 'is-valid', 'untouched');
            // mark field as valid/invalid
            if (!field.validity.valid) {
                field.classList.add('is-invalid');
                // focus on the first invalid field
                if (!hasError) {
                    hasError = true;
                    if (shouldFocus) {
                        field.focus();
                    }
                }
            }
            else {
                field.classList.add('is-valid');
            }
        }
        // validate the payment components: every hosted field must have reported
        // complete-without-error (wrapper classes alone miss untouched-empty fields,
        // and a failed Chargebee mount must never pass)
        const cbFields = [
            [this.cbComplete.number, this.pcCardNumber],
            [this.cbComplete.expiry, this.pcExp],
            [this.cbComplete.cvv, this.pcCvv],
        ];
        for (let [complete, field] of cbFields) {
            if (!field) {
                continue;
            }
            if (!this.cbCard || !complete || field.classList.contains('is-invalid')) {
                field.classList.add('is-invalid');
                if (!hasError) {
                    hasError = true;
                    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }
        return !hasError;
    }
    /**
     * Write the typed contact values back into FORM_STATE.userInput.profile (the gate
     * profile carries address/promo/consent — names/email/phone are authoritative from
     * these inputs at submit time, edits here win over what the gate captured).
     */
    syncProfileFromInputs() {
        const profile = this.FORM_STATE.userInput.profile ?? {
            address: this.addressBox?.innerText?.trim() ?? '',
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            promo: (0, Utils_1.promoCode)(this.FORM_STATE)
        };
        profile.firstName = this.contactInputs.firstName.value.trim();
        profile.lastName = this.contactInputs.lastName.value.trim();
        profile.email = this.contactInputs.email.value.trim();
        profile.phoneNumber = this.contactInputs.phoneNumber.value;
        if (this.contactInputs.company && !this.companyGroup?.classList.contains('d-none')) {
            profile.company = this.contactInputs.company.value.trim();
        }
        this.FORM_STATE.userInput.profile = profile;
    }
    /**
     * POST /api/createCustomer — exactly the fields the old ConfirmOfferStep sent.
     * @return whether the flow may proceed to payment.
     */
    async createCustomer() {
        try {
            // TODO: find a real way to deduplicate these
            let addonInternals = this.FORM_STATE.userInput.selection?.addons?.map(addon => addon.internal) || [];
            // deduplicate addonInternals
            addonInternals = Array.from(new Set(addonInternals));
            let res = await fetch((0, Utils_1.apiUrl)('/api/createCustomer'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...this.FORM_STATE.config.additionalParameters,
                    profile: this.FORM_STATE.userInput.profile,
                    // this landing's ?agent= wins over the cookie — see agentCredit()
                    agent: (0, Utils_1.agentCredit)(this.FORM_STATE),
                    referralCode: this.referralCode.value || '',
                    offerInternal: this.FORM_STATE.userInput.selection?.plan?.internal,
                    addonInternals: addonInternals,
                    // required service (SMS) consent captured on this step
                    serviceOptIn: this.serviceOptIn.checked,
                    // v4: spine + attribution → carried to Chargebee meta_data.attributionRef (phase 5)
                    profileUuid: this.FORM_STATE.cookies.uuid,
                    attribution: this.FORM_STATE.attribution
                })
            });
            let data = await res.json();
            // event (same position as the old flow: after the response, before the code check)
            (0, Utils_1.sendGEvent)('purchase', this.FORM_STATE);
            (0, Utils_1.sendEvent)('createdCustomer', this.FORM_STATE);
            if (data.code) {
                if (data.code === 'OK') {
                    this.FORM_STATE.ugpsData.cid = data.cid;
                    this.customerCreated = true;
                    return true;
                }
                else if (data.code === 'REDIRECT') {
                    (0, Utils_1.sendRedirect)(`https://wire3.com/thank-you`);
                    return false;
                }
                else if (data.code === 'EXPIRED') {
                    window.location.reload();
                    return false;
                }
            }
            // invalid order (old flow alert()'d here) — staged error instead
            this.renderError(`We were unable to create your order. Please review your details and try again, or contact support for assistance.`);
            return false;
        }
        catch (err) {
            this.renderError(`We were unable to reach our ordering system. Please try again — if the issue persists, contact support.`);
            return false;
        }
    }
    ;
    /**
     * Append v=2 (scheduler2 look) to the scheduling link —
     * safe whether or not the link already carries a query string.
     */
    withSchedulerV2(link) {
        try {
            const url = new URL(link, window.location.href);
            url.searchParams.set('v', '2');
            return url.toString();
        }
        catch (ignored) {
            return `${link}${link.includes('?') ? '&' : '?'}v=2`;
        }
    }
    /** Init the Chargebee instance + mount the hosted fields — once.
     *
     *  Async since the SEO pass: chargebee.js is injected here rather than in the page head.
     *  Callers (unlock/onShow) still fire and forget — the error path below is the whole
     *  failure contract, so nothing downstream awaits this. */
    async startPaymentComponents() {
        // a dead network/CDN leaves window.Chargebee absent, same as a blocked head tag did
        await (0, Utils_1.loadScript)(CHARGEBEE_SRC).catch(() => { });
        // cb not loaded, show error
        if (!window['Chargebee']) {
            this.renderError('We were unable to securely connect to our payment system. Please contact support for a payment update link.');
            return;
        }
        if (!this.cbInstance) {
            this.cbInstance = window['Chargebee'].init({
                site: `wire3`,
                // public-facing API key (you can't hack us with this ;p)
                publishableKey: `live_OGRoRMcdzLkccro7od60VswVFAz44XhuR`
            });
            this.mountPaymentComponents();
        }
    }
    async mountPaymentComponents() {
        await this.cbInstance.load("components");
        const card = this.cbInstance.createComponent("card", this.paymentComponentsOptions);
        this.cbCard = card;
        // track per-field completeness ourselves: the wrapper classes (.empty/.is-invalid)
        // only appear after the first change event, so untouched-empty fields would
        // otherwise pass validation and let createCustomer fire with no card
        const track = (name, field) => {
            field.on('change', (ev) => { this.cbComplete[name] = !!ev?.complete && !ev?.error; });
        };
        track('number', card.createField('number').at('#pc-card-number'));
        track('expiry', card.createField('expiry').at('#pc-card-expiry'));
        track('cvv', card.createField('cvv').at('#pc-card-cvv'));
        await card.mount();
        if (this.FORM_STATE.config.devMode) {
            window['cbInstance'] = this.cbInstance;
            window['cbCard'] = card;
        }
    }
    async submitPaymentComponents() {
        this.form.classList.add('submit');
        try {
            let token = await this.cbCard.tokenize();
            let res = await fetch((0, Utils_1.apiUrl)(`/api/createSubscription`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid: this.FORM_STATE.ugpsData.cid,
                    token: token.token
                })
            });
            let data = await res.json();
            (0, Utils_1.setLoadingButton)(this.submit, false);
            if (!res.ok || !data.error) {
                if (data.code === 'OK') {
                    // resolve the sibling step by type — step indices differ per mode
                    this.FORM_STATE.steps.find(s => s instanceof ScheduleStep_1.ScheduleStep)?.unlock();
                    // event
                    (0, Utils_1.sendGEvent)('add_payment_info', this.FORM_STATE, {
                        cid: this.FORM_STATE.ugpsData.cid ?? 'N/A',
                        canSchedule: this.FORM_STATE.ugpsData?.canInstall && !!data.schedulingLink,
                        resCode: res.status,
                        dataCode: data.code ?? 'N/A'
                    });
                    (0, Utils_1.sendEvent)('addedPayment', this.FORM_STATE);
                    // This spine has now produced an order. Keep it for the rest of this session
                    // (scheduling still hangs off the same profileUuid), but make the NEXT form load
                    // on this browser mint a fresh one — otherwise the rep's next customer on the
                    // same tablet folds into this customer's identity record.
                    (0, Attribution_1.markSpineSpent)();
                    // success - either redirect to scheduling link or go to schedule step
                    if (this.FORM_STATE.ugpsData?.canInstall && data.schedulingLink) {
                        (0, Utils_1.setLoadingButton)(this.submit, true, 'Redirecting to Scheduling...');
                        window.location.href = this.withSchedulerV2(data.schedulingLink);
                    }
                    else {
                        this.FORM_STATE.frame.stepper.next();
                    }
                }
                else if (data.code === 'PAYMENT_REQUIRED') {
                    this.form.classList.remove('submit');
                    this.renderError(data?.message ?? `Your payment was successfully added but our payment gateway disagreed (cache issue). Please try again.`);
                    (0, Utils_1.sendEvent)('payment_required', this.FORM_STATE, { error: JSON.stringify(data) });
                }
                else if (data.code === 'INVALID') {
                    this.form.classList.remove('submit');
                    this.renderError(data?.message ?? `There was an error adding your plan/addons to your subscription. Please fill out the full form again, or contact support for assistance.`);
                    (0, Utils_1.sendEvent)('payment_order_invalid', this.FORM_STATE, { error: JSON.stringify(data) });
                }
                else {
                    this.form.classList.remove('submit');
                    this.renderError(data?.message ?? `Error submitting. Please fill out the full form again, or contact support for assistance.`);
                    (0, Utils_1.sendEvent)('payment_unknown_code', this.FORM_STATE, { error: JSON.stringify(data) });
                }
            }
            else {
                (0, Utils_1.setLoadingButton)(this.submit, false);
                this.form.classList.remove('submit');
                this.renderError(data?.message ?? `Unable to process card information. Please check your details and try again.`);
                (0, Utils_1.sendEvent)('payment_failure', this.FORM_STATE, { error: JSON.stringify(data) });
            }
        }
        catch (err) {
            console.warn(`[OF] Error tokenizing card:`, err);
            (0, Utils_1.setLoadingButton)(this.submit, false);
            this.form.classList.remove('submit');
            this.renderError(err?.message ?? `Unable to process card information. Please check your details and try again - if issue persists, please reload and try again.`);
            (0, Utils_1.sendEvent)('payment_error', this.FORM_STATE, { error: JSON.stringify(err), message: err?.message || 'N/A' });
        }
    }
    /** Staged error alert into #details-message (dismissable via the delegated
     *  .dismiss handler; the #x symbol ships with step-1 in the same document). */
    renderError(message) {
        if (!this.message) {
            return;
        }
        this.message.innerHTML = `
			<div class="alert alert-danger mt-3" role="alert">
				<button class="dismiss" type="button" aria-label="Close">
					<svg class="fa sm" viewBox="0 0 640 640"><use xlink:href="#x" /></svg>
				</button>
				<h2 class="alert-title">There's been an issue...</h2>
				<hr class="mt-1 mb-2">
				<p>${message}</p>
			</div>
		`.trim();
    }
}
exports.DetailsStep = DetailsStep;
